import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react'
import { Document, DocumentCreate, DocumentUpdate } from '../types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { documentsAPI } from '@api/documents'
import { useAuthStore } from './authStore'
import { log, logError, logDebug } from '@utils/logger'
import { formatValidationErrors } from '@utils/errorMessages'
import toast from 'react-hot-toast'

interface DocumentContextType {
  // State
  currentDocument: Document | null
  documents: Document[]
  isLoading: boolean
  isSaving: boolean
  autoSave: boolean
  
  // Actions
  createDocument: (data: DocumentCreate) => Promise<Document>
  updateDocument: (id: string, data: DocumentUpdate) => Promise<Document>
  deleteDocument: (id: string) => Promise<void>
  setCurrentDocument: (document: Document | null) => void
  saveCurrentDocument: (content?: string) => Promise<void>
  setAutoSave: (enabled: boolean) => void
  refreshDocuments: () => void
  // New: Method to trigger debounced autosave from editor
  triggerAutosave: (content?: string) => void
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined)

type DocumentAction =
  | { type: 'SET_CURRENT_DOCUMENT'; payload: Document | null }
  | { type: 'SET_AUTO_SAVE'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }

interface DocumentStateType {
  currentDocument: Document | null
  autoSave: boolean
  isSaving: boolean
}

const initialState: DocumentStateType = {
  currentDocument: null,
  autoSave: true, // Always enabled - auto-save is a core feature
  isSaving: false,
}

function documentReducer(state: DocumentStateType, action: DocumentAction): DocumentStateType {
  switch (action.type) {
    case 'SET_CURRENT_DOCUMENT':
      return {
        ...state,
        currentDocument: action.payload,
      }
    
    case 'SET_AUTO_SAVE':
      return {
        ...state,
        autoSave: action.payload,
      }
    
    case 'SET_SAVING':
      return {
        ...state,
        isSaving: action.payload,
      }
    
    default:
      return state
  }
}

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(documentReducer, initialState)
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()

  // Fetch documents - only when authenticated
  const { data: documents = [], isLoading } = useQuery<Document[]>({
    queryKey: ['documents'],
    queryFn: () => documentsAPI.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
    enabled: isAuthenticated, // Only fetch when user is authenticated
  })

  // Create document mutation
  const createMutation = useMutation({
    mutationFn: documentsAPI.create,
    onSuccess: (newDocument) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: newDocument })
    },
    onError: (error: any) => {
      logError('Failed to create document:', error)
      // Extract error message from error object
      let errorMessage = 'Failed to create document'
      
      if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = formatValidationErrors(error.response.data.detail)
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail
        } else if (typeof error.response.data.detail === 'object') {
          // Handle Pydantic validation error objects
          if (error.response.data.detail.msg) {
            errorMessage = String(error.response.data.detail.msg)
          } else if (error.response.data.detail.message) {
            errorMessage = String(error.response.data.detail.message)
          } else {
            errorMessage = formatValidationErrors([error.response.data.detail])
          }
        }
      } else if (error?.message) {
        errorMessage = String(error.message)
      }
      
      // Ensure errorMessage is always a string
    },
  })

  // Update document mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DocumentUpdate }) =>
      documentsAPI.update(id, data),
    onSuccess: (updatedDocument) => {
      // Update the specific document in cache without refetching
      queryClient.setQueryData(['document', updatedDocument.id], updatedDocument)
      
      // Only invalidate the documents list (not refetch immediately)
      // This marks it as stale but won't trigger a refetch unless the list is actively being viewed
      queryClient.invalidateQueries({ 
        queryKey: ['documents'],
        refetchType: 'none' // Don't automatically refetch - only mark as stale
      })
      
      if (state.currentDocument?.id === updatedDocument.id) {
        // Update current document with server response (includes updated_at timestamp)
        dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: updatedDocument })
      }
      if (!state.autoSave) {
      }
      // Mark document as saved successfully - this will be used by editorStore
      // to mark isDirty as false
    },
    onError: (error: any, variables: { id: string; data: DocumentUpdate }) => {
      // Handle 404 errors - document might have been deleted (e.g., from another device)
      if (error?.response?.status === 404 || error?.status === 404) {
        const deletedDocId = variables.id
        const documentTitle = state.currentDocument?.title || 'This document'
        
        // Only handle if this is the current document
        if (state.currentDocument?.id === deletedDocId) {
          // Clear current document if it was deleted
          dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: null })
          
          // Notify user that the document was deleted
          toast.error(
            `${documentTitle} has been deleted. You will be redirected to your documents.`,
            { duration: 5000 }
          )
          
          // Dispatch event to notify components (like EditorPage) that document was deleted
          window.dispatchEvent(new CustomEvent('document-deleted', {
            detail: { 
              documentId: deletedDocId, 
              documentTitle 
            }
          }))
        }
      } else {
        // Log other errors
        logError('Failed to update document:', error)
      }
      // Don't mark as clean on error - keep isDirty true so user can retry
    },
    onSettled: () => {
      dispatch({ type: 'SET_SAVING', payload: false })
    },
  })

  // Delete document mutation
  const deleteMutation = useMutation({
    mutationFn: documentsAPI.delete,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (state.currentDocument?.id === deletedId) {
        dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: null })
      }
    },
    onError: (error) => {
      logError('Failed to delete document:', error)
    },
  })

  // Actions
  const createDocument = useCallback(async (data: DocumentCreate): Promise<Document> => {
    return createMutation.mutateAsync(data)
  }, [createMutation])

  const updateDocument = useCallback(async (id: string, data: DocumentUpdate): Promise<Document> => {
    dispatch({ type: 'SET_SAVING', payload: true })
    return updateMutation.mutateAsync({ id, data })
  }, [updateMutation])

  const deleteDocument = useCallback(async (id: string): Promise<void> => {
    await deleteMutation.mutateAsync(id)
  }, [deleteMutation])

  const setCurrentDocument = useCallback((document: Document | null) => {
    dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: document })
  }, [])

  const saveCurrentDocument = useCallback(async (content?: string): Promise<void> => {
    if (!state.currentDocument) return
    
    // Prevent saving if already saving
    if (state.isSaving) {
      logDebug('[DocumentStore] Save already in progress, skipping...')
      return
    }
    
    // Note: Permission checking should be done at the component level
    // This function will fail with 400/403 if user doesn't have permission
    const { id, title } = state.currentDocument
    // Use provided content or fall back to document content
    const contentToSave = content !== undefined ? content : state.currentDocument.content
    
    try {
      await updateDocument(id, { title, content: contentToSave })
    } catch (error: any) {
      // Handle 404 errors - document might have been deleted (e.g., from another device)
      if (error?.response?.status === 404 || error?.status === 404) {
        const documentTitle = state.currentDocument?.title || 'This document'
        logDebug('[DocumentStore] Document not found during save (may have been deleted)')
        
        // Clear current document if it was deleted
        if (state.currentDocument?.id === id) {
          dispatch({ type: 'SET_CURRENT_DOCUMENT', payload: null })
        }
        
        // Notify user that the document was deleted
        toast.error(
          `${documentTitle} has been deleted. You will be redirected to your documents.`,
          { duration: 5000 }
        )
        
        // Dispatch event to notify components (like EditorPage) that document was deleted
        // This allows EditorPage to navigate away if needed
        window.dispatchEvent(new CustomEvent('document-deleted', {
          detail: { documentId: id, documentTitle }
        }))
        
        // Don't re-throw - this is expected for deleted documents
        return
      }
      // Re-throw other errors so they can be handled by callers
      throw error
    }
  }, [state.currentDocument, state.isSaving, updateDocument, dispatch])

  // Debounced autosave - saves 2 seconds after user stops typing
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  const triggerAutosave = useCallback((content?: string) => {
    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
    }

    // Only autosave if enabled and document exists
    if (!state.autoSave || !state.currentDocument || state.isSaving) {
      return
    }

    // Set new timeout for debounced save
    autosaveTimeoutRef.current = setTimeout(() => {
      saveCurrentDocument(content).catch((error) => {
        // 404 errors are already handled in saveCurrentDocument, so only log other errors
        if (error?.response?.status !== 404 && error?.status !== 404) {
          logError('[DocumentStore] Autosave failed:', error)
        }
        // Don't show toast for autosave failures to avoid spam
      })
    }, 2000) // 2 second delay after user stops typing
  }, [state.autoSave, state.currentDocument, state.isSaving, saveCurrentDocument])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }
    }
  }, [])

  const setAutoSave = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_AUTO_SAVE', payload: enabled })
    
    // Save to localStorage
    localStorage.setItem('autoSave', JSON.stringify(enabled))
  }, [])

  const refreshDocuments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['documents'] })
  }, [queryClient])

  // Load auto-save preference from localStorage
  useEffect(() => {
    const savedAutoSave = localStorage.getItem('autoSave')
    if (savedAutoSave !== null) {
      try {
        const enabled = JSON.parse(savedAutoSave)
        dispatch({ type: 'SET_AUTO_SAVE', payload: enabled })
      } catch (error) {
        logError('Failed to parse autoSave setting:', error)
      }
    }
  }, [])

  // Legacy interval-based autosave (fallback - runs every 60 seconds as backup)
  // This ensures document is saved even if debounced autosave doesn't trigger
  // NOTE: This should only save if there are actual changes (isDirty check should be done at component level)
  // For now, we rely on the debounced autosave and this is just a safety net
  // TODO: Pass isDirty state to this store or check content changes before saving
  useEffect(() => {
    if (!state.autoSave || !state.currentDocument) return

    const autoSaveInterval = setInterval(() => {
      // Only save if not already saving
      // Note: We can't check isDirty here since it's in editorStore
      // The debounced autosave should handle most cases, this is just a backup
      if (state.currentDocument && !state.isSaving) {
        // This will be a no-op if content hasn't changed (server will return same document)
        saveCurrentDocument().catch((error) => {
          // 404 errors are already handled in saveCurrentDocument, so only log other errors
          if (error?.response?.status !== 404 && error?.status !== 404) {
            logError('[DocumentStore] Interval autosave failed:', error)
          }
        })
      }
    }, 60000) // Auto-save every 60 seconds as backup (increased from 30 to reduce API calls)

    return () => clearInterval(autoSaveInterval)
  }, [state.autoSave, state.currentDocument, state.isSaving, saveCurrentDocument])

  const value: DocumentContextType = {
    // State
    currentDocument: state.currentDocument,
    documents,
    isLoading,
    isSaving: state.isSaving || updateMutation.isPending,
    autoSave: state.autoSave,
    
    // Actions
    createDocument,
    updateDocument,
    deleteDocument,
    setCurrentDocument,
    saveCurrentDocument,
    setAutoSave,
    refreshDocuments,
    triggerAutosave,
  }

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  )
}

export const useDocumentStore = () => {
  const context = useContext(DocumentContext)
  if (context === undefined) {
    throw new Error('useDocumentStore must be used within a DocumentProvider')
  }
  return context
}